import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ExcelDownloadModal from "../../components/requests/ExcelDownloadModal";
import { exportBulkRequests } from "../../api/excelTemplates";
import PageHeader from "../../components/layout/PageHeader";
import { StatusBadge as CandidateStatusBadge } from "../../components/ui/Badge";
import Table from "../../components/ui/Table";
import RequestActions, {
  ReviewedCell,
  fmtDate,
  selectColumn,
} from "../../components/requests/RequestActions";
import Button from "../../components/ui/Button";
import Spinner from "../../components/ui/Spinner";
import EmptyState from "../../components/ui/EmptyState";
import Modal from "../../components/ui/Modal";
import Textarea from "../../components/ui/Textarea";
import Tabs from "../../components/ui/Tabs";
import { BULK_CATEGORY_TABS } from "../../utils/bulkRequestCategories";
import RequestFilters, {
  StatusBadge,
  useRequestFilters,
} from "../../components/requests/RequestFilters";
import {
  listBulkRequests,
  approveBulkRequest,
  rejectBulkRequest,
} from "../../api/bulkRequests";
import {
  BulkActionBar,
  useRowSelection,
} from "../../components/requests/BulkSelection";

export const BULK_REQUESTS_QUERY_KEY = ["bulk-requests"];
export const BULK_REQUESTS_COUNT_QUERY_KEY = ["bulk-requests", "count"];

export function RejectRemarksModal({
  open,
  onClose,
  onConfirm,
  loading,
  title = "Reject bulk request",
}) {
  const [remarks, setRemarks] = useState("");

  const handleClose = () => {
    setRemarks("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => onConfirm(remarks)}
            loading={loading}
            disabled={!remarks.trim()}
          >
            Reject request
          </Button>
        </>
      }
    >
      <Textarea
        label="Remarks"
        required
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
      />
    </Modal>
  );
}

function CandidatesButton({ candidates }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="font-medium text-primary hover:underline"
      >
        {candidates.length} candidate{candidates.length === 1 ? "" : "s"}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Candidates in this request"
      >
        <ul className="space-y-2 text-sm">
          {candidates.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2">
              <span>
                <Link
                  to={`/candidates/${c.id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {c.full_name}
                </Link>{" "}
                <span className="text-xs text-gray-400">{c.reference_no}</span>
              </span>
              <span className="text-xs text-gray-400">
                {c.step_at_request || "—"}
              </span>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

function ExcelDownloadButton({ row }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm font-medium text-primary hover:underline"
      >
        Excel
      </button>
      {open && (
        <ExcelDownloadModal
          open
          onClose={() => setOpen(false)}
          ids={[row.id]}
          onExport={exportBulkRequests}
        />
      )}
    </>
  );
}

const CATEGORY_LABELS = Object.fromEntries(
  BULK_CATEGORY_TABS.map((t) => [t.value, t.label]),
);

const columnsFor = (selection, refetch, showCategory) => [
  selectColumn(
    selection,
    (r) => r.id,
    (r) => r.status === "pending",
  ),
  {
    key: "type",
    label: "Request",
    render: (row) => (
      <div>
        <span className="font-medium text-gray-900">{row.type}</span>
        {row.candidate_status && (
          <span className="ml-2 align-middle">
            <CandidateStatusBadge status={row.candidate_status} />
          </span>
        )}
        <div className="text-xs text-gray-400">
          {row.workflow_category}
          {row.workflow_step && <> — {row.workflow_step}</>}
        </div>
      </div>
    ),
  },
  ...(showCategory
    ? [
        {
          key: "category",
          label: "Request Type",
          render: (row) => CATEGORY_LABELS[row.category] || row.category || "—",
        },
      ]
    : []),
  {
    key: "candidates",
    label: "Candidates",
    render: (row) => <CandidatesButton candidates={row.candidates} />,
  },
  {
    key: "requested",
    label: "Requested",
    render: (row) => (
      <div>
        {row.requested_by}
        <div className="text-xs text-gray-400">{fmtDate(row.requested_at)}</div>
        {row.note && (
          <div className="text-xs italic text-gray-500">"{row.note}"</div>
        )}
      </div>
    ),
  },
  {
    key: "status",
    label: "Status",
    render: (row) => (
      <div>
        <StatusBadge status={row.status} />
        <ReviewedCell row={row} />
      </div>
    ),
  },
  {
    key: "excel",
    label: "",
    render: (row) => <ExcelDownloadButton row={row} />,
  },
  {
    key: "actions",
    label: "",
    render: (row) => (
      <RequestActions
        pending={row.status === "pending"}
        onApprove={() => approveBulkRequest(row.id)}
        onReject={(reason) => rejectBulkRequest(row.id, reason)}
        onResolved={refetch}
        approvedMessage="Bulk request approved."
        rejectedMessage="Bulk request rejected."
        RejectModal={RejectRemarksModal}
      />
    ),
  },
];

const searchFields = (row) => [
  row.type,
  row.workflow_category,
  row.workflow_step,
  row.requested_by,
  ...row.candidates.flatMap((c) => [c.full_name, c.reference_no]),
];
const requestedAt = (row) => row.requested_at;

export default function BulkRequestApprovals() {
  const queryClient = useQueryClient();
  const { filters, set, apply, reset, dirty, candidate } = useRequestFilters(
    searchFields,
    requestedAt,
  );
  const [tab, setTab] = useState("action");
  const query = useQuery({
    queryKey: [
      ...BULK_REQUESTS_QUERY_KEY,
      filters.status,
      tab,
      candidate.params,
    ],
    queryFn: () =>
      listBulkRequests({
        ...candidate.params,
        status: filters.status,
        category: tab || undefined,
      }),
  });
  const allRows = query.data || [];
  const rows = apply(allRows);
  const selection = useRowSelection(
    rows,
    (r) => r.id,
    (r) => r.status === "pending",
  );

  const pendingIds = new Set(
    rows.filter((r) => r.status === "pending").map((r) => r.id),
  );
  const [downloadOpen, setDownloadOpen] = useState(false);

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: BULK_REQUESTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: BULK_REQUESTS_COUNT_QUERY_KEY });
  };

  return (
    <div>
      <PageHeader
        title="Bulk Request Approval"
        description="Bulk requests raised against multiple candidates at once — approve or reject the whole batch here."
      />

      <Tabs
        tabs={[{ value: "", label: "All" }, ...BULK_CATEGORY_TABS]}
        active={tab}
        onChange={setTab}
      />
      <div className="mt-4" />

      <>
        <RequestFilters
          candidate={candidate}
          filters={filters}
          set={set}
          reset={reset}
          dirty={dirty}
          total={allRows.length}
          shown={rows.length}
        />

        <BulkActionBar
          selection={selection}
          actionableIds={pendingIds}
          selectAllLabel={`Select all (${selection.selectableCount})`}
          extraActions={
            <Button variant="secondary" onClick={() => setDownloadOpen(true)}>
              Download Excel
            </Button>
          }
          onApprove={approveBulkRequest}
          onReject={rejectBulkRequest}
          onDone={refetch}
        />

        {downloadOpen && (
          <ExcelDownloadModal open onClose={() => setDownloadOpen(false)} ids={selection.selectedIds} onExport={exportBulkRequests} />
        )}

        {query.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={dirty ? "No matching requests" : "Nothing pending"}
            message={
              dirty
                ? "No requests match the current filters."
                : "No bulk requests are currently awaiting review."
            }
          />
        ) : (
          <Table
            columns={columnsFor(selection, refetch, tab === "")}
            data={rows}
          />
        )}
      </>
    </div>
  );
}
